import {
  OriginButton,
  Input,
  FormContext,
  FormItem,
  Typography,
  Tooltip,
} from '@frontend/design_system'
import { InfoIcon } from 'ui/icons'
import arrowRight from 'assets/img/arrowRight.webp'
import { joinClasses } from 'utils/joinClasses'
import {
  LOCATION_TABS,
  NEXT_BUTTON_TITLE,
  LOCATION_ADDING_FORM_VALUES,
} from 'texts/locationDetails'
import { RESERVATION_FORM_FIELDS } from '../../constants'
import { VEHICLE_DETAILS } from 'texts/vehicleDetails'
import { DriverAgeRanges } from 'ui/components/DriverAgeRanges'
import { ContextType } from 'ui/molecules/Form/types'
import { getRequiredRule } from 'utils/getValidationRules'
import { useMemo, useContext } from 'react'
import { useReservationRules } from './hooks/useReservationRules'
import { ReservationFormProps } from './types'
import { allCharactersExceptNumbers } from 'constants/regex'
import { scrollTop } from 'utils/scrollTop'
import { FormItemChildren } from 'types/form'
import styles from './styles.module.scss'

export const ReservationForm = ({
  driverAgeRangeError,
  resetDriverAgeError,
  formValues,
  changeActiveKey,
  validateDriverAgeRange,
  validateWorkingHours,
}: ReservationFormProps) => {
  const rules = useReservationRules(formValues)
  const { values, checkValidation } = useContext<ContextType>(FormContext)

  const validationRules = (message: string) =>
    useMemo(() => getRequiredRule(message), [])

  const goNextTab = () => {
    const { newErrors } = checkValidation()
    const preparedWorkingHours = validateWorkingHours()
    const error = validateDriverAgeRange(values)

    const filteredErrors = Object.keys(RESERVATION_FORM_FIELDS).reduce(
      (acc: Record<string, unknown>, item) => {
        if (newErrors[item]) {
          acc[item] = newErrors[item]
        }
        return acc
      },
      {}
    )

    if (
      !Object.keys(filteredErrors).length &&
      !error &&
      preparedWorkingHours.length
    ) {
      scrollTop()
      changeActiveKey(LOCATION_TABS.WAITING_HOURS)
    }
  }

  const renderTooltip = (value: string) => (
    <div className={styles['form-tooltip-wrapper']}>
      <div className={styles['form-tooltip']}>
        <Tooltip arrowPosition="right" popupContent={value} placement="bottom">
          <InfoIcon color="blue700" size="small" />
        </Tooltip>
      </div>
    </div>
  )

  return (
    <>
      <div className={styles['form-items-row']}>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.MINIMUM_PERIOD_DAY.ID}
          className={joinClasses(styles['form-item'], styles['form-item-left'])}
          rules={rules.minimumPeriodDay}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={
                LOCATION_ADDING_FORM_VALUES.MINIMUM_PERIOD_DAY.PLACEHOLDER
              }
              size="large"
              error={error}
              spaceForError="auto"
              isRequired
              onChange={(value: string) =>
                onChange(value.replace(allCharactersExceptNumbers, ''))
              }
              label={LOCATION_ADDING_FORM_VALUES.MINIMUM_PERIOD_DAY.LABEL}
            />
          )}
        </FormItem>
        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.MAXIMUM_PERIOD_DAY.ID}
          className={styles['form-item']}
          rules={rules.minMaxPeriodDay}
        >
          {({ value, error, onChange }: FormItemChildren) => (
            <Input
              value={value}
              placeholder={
                LOCATION_ADDING_FORM_VALUES.MAXIMUM_PERIOD_DAY.PLACEHOLDER
              }
              size="large"
              type="integer"
              error={error}
              spaceForError="auto"
              onChange={(value: string) =>
                onChange(value.replace(allCharactersExceptNumbers, ''))
              }
              label={LOCATION_ADDING_FORM_VALUES.MAXIMUM_PERIOD_DAY.LABEL}
            />
          )}
        </FormItem>
      </div>
      <FormItem
        id={LOCATION_ADDING_FORM_VALUES.START_RESERVATION_HOUR.ID}
        className={joinClasses(styles['form-item'], styles['form-item-left'])}
        rules={validationRules(
          LOCATION_ADDING_FORM_VALUES.START_RESERVATION_HOUR.REQUIRED_MESSAGE
        )}
      >
        {({ value, error, onChange }: FormItemChildren) => (
          <Input
            value={value}
            placeholder={
              LOCATION_ADDING_FORM_VALUES.START_RESERVATION_HOUR.PLACEHOLDER
            }
            size="large"
            error={error}
            iconRight={renderTooltip(
              LOCATION_ADDING_FORM_VALUES.START_RESERVATION_HOUR.TOOLTIP
            )}
            spaceForError="auto"
            onChange={(value: string) =>
              onChange(value.replace(allCharactersExceptNumbers, ''))
            }
            isRequired
            label={LOCATION_ADDING_FORM_VALUES.START_RESERVATION_HOUR.LABEL}
          />
        )}
      </FormItem>
      <div>
        <Typography
          className={styles['driver-age-range-header']}
          Tag="h5"
          name="H5"
        >
          {VEHICLE_DETAILS.DRIVER_AGE_RANGE_TITLE}
        </Typography>

        <FormItem
          id={LOCATION_ADDING_FORM_VALUES.DRIVER_AGE_RANGE.ID}
          rules={rules.dailyRangeRules}
        >
          {({ onChange, value }: FormItemChildren) => (
            <DriverAgeRanges
              driverAgeRangeData={value}
              isRequired
              error={driverAgeRangeError}
              onDriverAgeRangeDataChange={(value) => {
                onChange(value)
                resetDriverAgeError()
              }}
              FooterComponent={
                <div className={styles['form-next-button']}>
                  <OriginButton
                    variant="link"
                    size="large"
                    onClick={goNextTab}
                    label={NEXT_BUTTON_TITLE}
                    typographyName="Button1"
                    iconRight={<img src={arrowRight} width={16} height={16} />}
                  />
                </div>
              }
            />
          )}
        </FormItem>
      </div>
    </>
  )
}
